/*
    Show Devices

    A controller to provide a filtered list of devices.

    As much salesware for IoT world and also the combination of
    two seperate views.
*/

import _ from 'underscore';
import Backbone from 'backbone';
import Radio from 'backbone.radio';
import Handlebars from 'handlebars';
import CollectionTableView from '@/views/collection_table';
import DialogInterface from '@/interfaces/dialog';
import DeviceView from '@/views/device';
import StandardListLayout from '@/views/standard_list_layout';
import DeviceFilter from '@/views/device_filter_layout';
import DeviceGroupModel from '@/models/v5/device_group';
import DevicesBulkControls from '@/views/devices_bulk_controls';
import PaginationControls from '@/views/pagination_controls';
import ResponseResultsView from '@/views/response_results_view';
import DeviceListEmptyView from '@/views/device_list_empty_view';
import collection_template from '@/../templates/collection-table.hbs';
import strings_specific from 'amdi18n-loader!@/nls/device_group_list';
import strings_common from 'amdi18n-loader!@/nls/common';
import my_development_devices_list_layout from '@/../templates/my-development-devices-list-layout.hbs';
var globalCh            = Radio.channel('global');
var apiCh               = Radio.channel('api');
var routerCh            = Radio.channel('router');
var strings             = _.extend({}, strings_common, strings_specific);

var BULK_ACTIONS = ['restart', 'unassign', 'move'];

var columns_template = [{
        name: '',
        class_name: 'select',
        sortable: false,
        select_all: true
    }, {
        name: strings.device_id,
        class_name: 'name',
        sortable: false,
        sort_by: getDisplayName,
    }, {
        name: 'Status',
        class_name: 'status',
        sortable: false,
    }, {
        name: strings.owner,
        class_name: 'owner',
        sortable: false,
        sort_by: getOwnerName,
    }, {
        name: strings.account,
        class_name: 'account',
        sortable: false,
        sort_by: getAccountName,
    }, {
        name: 'Product',
        class_name: 'product',
        sortable: false
    }, {
        name: strings.groups,
        class_name: 'group',
        sortable: false,
        sort_by: getGroupName
    }, {
        name: 'Last Enrolled',
        class_name: 'enrolled',
        sortable: false,
        sort_by: 'last_blinkup_at'
    }, {
        name: 'Manage'
    }
];
var getDisplayName = function() { return this.getDisplayName(); };
var getOwnerName = function() { return this.getOwnerName(); };
var getAccountName = function() {
    var owner = this.devicegroup.rel.get('owner');
    return owner.get('username');
};
var getGroupName = function() {
    var model = this.getModel();
    return model && model.get('name') || '';
};

// Takes some filter functions and combines them into one filter function
function combine_filters() {
    var funcs = Array.prototype.slice.call(arguments);
    return function(item) {
        return funcs.reduce(function(acc, func, memo) {
            return acc && func(item);
        }, true);
    };
}

/**
 * @param Object filter looks like {
 *      product: Product
 *  OR  devicegroup: Devicegroup
 *  etc.
 * }
 *
 * @param Array columns, like the columns you would use for CollectionTableView
 *          OR null, in which case this controller chooses the defaults
 * @param String title of the devices page to be shown,
 *          OR null, using calculations in this controller
 * @param Object child_view_options, options for the child item view
 *        of the CollectionTableView. For instance, choose the columns to
 *        display or hide here (using DeviceView's 'show' option
 */
function show_devices(filter, columns, title, child_view_options, show_blinkup) {
    // Needed to prevent columns array from persisting between views
    columns = columns || columns_template.slice();

    var active = globalCh.request('active_account');
    var devices = apiCh.request('device');
    var app_layout = globalCh.request('app_layout');
    var show_unassign_action = true;
    var show_move_action = true;
    var show_restart_action = true;

    // Production device lists shouldn't show bulk unassign
    if (filter.devicegroup && filter.devicegroup.type === 'production_devicegroup') {
        show_unassign_action = false;
    }

    // Test zone should not have move or restart actions
    if (filter.devicegroup &&
        (filter.devicegroup.type === 'pre_dut_devicegroup' || filter.devicegroup.type === 'dut_devicegroup')) {
      show_move_action = false;
      show_restart_action = false;
    }
    // hacky hack hack TODO refactor this controller
    // in the mean time we need to use a different column name
    // when we're in a devicegroup context
    var group_column = _(columns).findWhere({ class_name: 'group' });

    function generated_title(filter){
        if ( filter.owner ) {
            return 'My Development Devices';
        }

        if ( filter.account ) {
            group_column.name = "Device Group";
            return 'Account Development Devices';
        }

        if ( filter.devicegroup ) {
            group_column.name = filter.devicegroup.type_readable();
            if ( filter.devicegroup.type === 'factoryfixture_devicegroup' ) {
                return 'Test Fixtures';
            }
            if ( filter.devicegroup.type === 'production_devicegroup' ) {
                return 'Production Devices';
            }
            return filter.devicegroup.type_readable() + ' Devices';
        } else if ( filter.product && filter.devicegroup_type ) {
            // Hacky TODO, find a better way to get these devicegroup strings
            var dg = new DeviceGroupModel({ type: filter.devicegroup_type });
            group_column.name = dg.type_readable();
            return 'Manage ' + dg.type_readable() + ' Devices';
        } else {
            return 'Manage Development Devices';
        }
    }

    var devices_layout = new StandardListLayout({
        template: Handlebars.compile(my_development_devices_list_layout),
        title: title || generated_title(filter),
        show_blinkup: show_blinkup
    });
    var controls_view = new DevicesBulkControls({
        show_unassign_action: show_unassign_action,
        show_restart_action: show_restart_action,
        show_move_action: show_move_action
    });

    var pagination_controls = new PaginationControls({
        collection: devices
    });

    var device_filter = new DeviceFilter({
        filter: filter
    });

    var devices_list = new CollectionTableView({
        template: Handlebars.compile(collection_template),
        collection: new Backbone.Collection(),
        childView: DeviceView,
        childViewOptions: function() {
            var options = {
                tagName: 'tr',
            };
            if (filter.owner || filter.account) {
                options.show = { type: true };
            }

            return _.extend(
                options,
                _.result(CollectionTableView.prototype, 'childViewOptions'),
                child_view_options
            );
        },
        emptyView: DeviceListEmptyView,
        emptyViewOptions: {
            devicegroup: filter.devicegroup
        },
        class_name: 'devices-list',
        default_sort_column: 7,
        columns: columns,
        default_sort_desc: true
    });
    // when a device is added or removed the global device collection,
    // look to see if it matches the paramters for this view
    // spaghetti!!!
    devices_list.collection.listenTo(devices, 'add change:rel', function(device) {
        var rel = device.rel;
        if (device.type != 'device') return;
        var should_add = (
            (filter.owner && filter.owner === rel.get('owner'))
        ||  (filter.devicegroup && filter.devicegroup === rel.get('devicegroup'))
        ||  (filter.account && filter.account === rel.get('account'))
        ||  (filter.product && filter.product === rel.get('product'))
        ||  (filter.owner && filter.owner === rel.get('account'))
        ||  (filter.account && filter.account === rel.get('owner'))
        );
        if (should_add && device.collection) {
            devices_list.collection.add(device, { at: 0 });
        } else {
            devices_list.collection.remove(device);
        }
    });
    
    apiCh.on('remote', function(event) {
        if ( event && event.action == 'delete' ) {
            if ( self.collection.contains(event.model) ) {
                self.collection.remove(event.model);
            }
        }
        if ( event && event.action == 'update' ) {
            if ( devices_list.collection.contains(event.model) ) {
                if ( filter.devicegroup && event.model && event.model.rel.get('devicegroup').id != filter.devicegroup.id ) {
                     devices_list.collection.remove(event.model);
                }
            }
        }
    });

    function remove_device(device) {
        devices_list.collection.remove({ id: device.id });
    }
    devices_list.collection.listenTo(devices, 'remove', function(d) {
        remove_device(d);
    });
    function bulk_action_state_set(devices) {
        var should_disable = [];
        var active = globalCh.request('active_account');
        var primary = globalCh.request('primary_account');
        if (devices.length) {
            _.each(devices, function (device) {
                var devicegroup = device.rel.get('devicegroup');
                var type = (devicegroup && devicegroup.type) || 'unassigned';
                switch (type) {
                    case 'unassigned':
                        should_disable = ['unassign', 'restart'];
                        break;
                    case 'pre_production_devicegroup':
                        should_disable = [];
                        if (!device.get('agent_id')) {
                            should_disable.push('restart');
                        }
                        break;
                    case 'production_devicegroup':
                        if (!active.permits(primary, 'restart_production_device'))
                            should_disable = ['restart'];
                        break;
                    case 'pre_dut_devicegroup':
                        should_disable = ['restart', 'move'];
                        break;
                    case 'dut_devicegroup':
                        should_disable = ['restart', 'move'];
                        break;
                }
            });
        } else {
            should_disable = ['unassign', 'restart'];
        }

        controls_view.disable_actions.apply(controls_view, should_disable);
        controls_view.enable_actions.apply(controls_view, _.difference(BULK_ACTIONS, should_disable));
    }
    // If a childview is selected, the controls view needs to know, devices_list can help
    devices_list.listenTo(devices_list, 'child_selected', bulk_action_state_set);
    devices_list.listenTo(devices_list, 'deselect_all', function () { controls_view.trigger('toggle_actions'); });
    devices_list.listenTo(devices_list, 'select_all', bulk_action_state_set);

    // function to show response results
    var show_response_results = function(success_messages, error_messages, models, message){
        if ( error_messages.length < 1 ){
            var devices_string = success_messages.length === 1 ? 'device was' : 'devices were';
            var devices_succeeded = success_messages.length;
            globalCh.request('layout_alert', ' ' + devices_succeeded + ' ' + devices_string + message, 'success', 5000);
        } else {
            var response_dialog = ResponseResultsView.extend(DialogInterface).extend({
                initialize: function(){
                    this.destroyOnClose();
                }
            });
            Array.prototype.push.apply(success_messages, error_messages);
            globalCh.request('showDialog', new response_dialog({ resp: success_messages }));
        }
        devices_list.render();
    };

    var last_fetched_filter;

    function set_filter(filter) {
        last_fetched_filter = filter;
        fetch_devices();
    }

    function success_show_new_page(coll, resp, opts) {
        last_fetched_filter = opts.filter;
        devices_list.collection.reset(
            devices.filter(combine_filters(opts.filter))
        );
        devices_list.render();
        pagination_controls.update_prev_next_from_response(resp);
        }

    /*
        * Show Devices Layout
        *
        * Called after devices.fetch completes
        */
    function show_devices_layout() {
        /* This sets the router location */
        var route_key;
        var option_1;
        var option_2;
        switch ( device_filter.model.router_command() ) {
            case 'devices_by_owner' :
                route_key = 'my_devices';
                option_1 = device_filter.model.get('owner');
                break;
            case 'product_devices':
                route_key = 'product_devices';
                option_1 = device_filter.model.get('product');
                break;
            case 'devicegroup_devices':
                route_key = 'devicegroup_devices';
                option_1 = device_filter.model.get('product');
                option_2 = device_filter.model.get('devicegroup');
                break;
            case 'product_devicegroup_type':
                route_key = 'product_devicegroups';
                option_1 = device_filter.model.get('product');
                option_2 = device_filter.model.get('devicegroup_type');
                break;
            default :
                route_key = 'devices';
                option_1 = active;
        }
        routerCh.request('set_location', route_key, option_1, option_2);

        if ( filter.owner ) {
            globalCh.request('set_selection', { type: 'app', id: 'devices'});
            globalCh.request('show_secondary_nav', { empty: true });
        }

        if ( filter.account ) {
            globalCh.request('set_selection', { type: 'app', id: 'device_management' });
            globalCh.request('show_account_secondary_nav', { selected: 'account-devices' });
        }

        if ( filter.devicegroup ) {
            let area;
            switch ( filter.devicegroup.type ) {
                case 'production_devicegroup' :
                    area = 'production';
                    globalCh.request('show_devicegroup_secondary_nav', filter.devicegroup, { selected: 'device-management' });
                    break;
                case 'factoryfixture_devicegroup' :
                    area = 'production';
                    globalCh.request('show_devicegroup_secondary_nav', filter.devicegroup, { selected: 'device-management' });
                    break;
                case 'pre_factoryfixture_devicegroup': 
                    area = 'test';
                    globalCh.request('show_dev_devicegroup_secondary_nav', filter.devicegroup, { selected: 'device-management' });
                    break;
                case 'pre_production_devicegroup': 
                    area = 'test';
                    globalCh.request('show_devicegroup_secondary_nav', filter.devicegroup, { selected: 'device-management' });
                    break;
                case 'dut_devicegroup' :
                    area = 'production';
                    globalCh.request('show_devicegroup_secondary_nav', filter.devicegroup, { selected: 'device-management' });
                    break;
                case 'pre_dut_devicegroup' :
                    area = 'test';
                    globalCh.request('show_dev_devicegroup_secondary_nav', filter.devicegroup, { selected: 'device-management' });
                    break;
                default :
                    globalCh.request('show_dev_devicegroup_secondary_nav', filter.devicegroup, { selected: 'device-management' });
                    break;
            }
            globalCh.request('set_selection', { type: filter.devicegroup.type, id: filter.devicegroup.id, v5_object: filter.devicegroup, area: area });
        } else if ( filter.product ) {
            globalCh.request('set_selection', { type: 'product', id: filter.product.id, v5_object: filter.product, area: filter.area } );
            if ( filter.area === 'production' ){
                globalCh.request('show_production_zone_secondary_nav', filter.product, { selected: 'device-management' });
            } else {
                globalCh.request('show_development_zone_secondary_nav', filter.product, { selected: 'device-management' });
            }
        }

        var raw_fetched_devices = devices.filter(last_fetched_filter);
        devices_list.collection.reset(raw_fetched_devices);

        // Brute force ugly way to listen when devices are unassigned
        // from a devicegroup list
        if (filter.devicegroup) {
            devices_list.collection.each(function(device) {
                function on_change_devicegroup(rel, new_devicegroup) {
                    if (new_devicegroup !== filter.devicegroup) {
                        devices_list.collection.remove(device);
                    }
                }
                devices_list.listenToOnce(
                    device.rel,
                    'change:devicegroup',
                    on_change_devicegroup
                );
            });
        }

        // show filters in the view with this
        // devices_layout.showChildView('filter', device_filter);

        app_layout.showChildView('container', devices_layout);

        devices_layout.showChildView('bulk_controls', controls_view);
        devices_layout.showChildView('pagination_controls', pagination_controls);
        devices_layout.showChildView('content', devices_list);
        }

    function fetch_devices(){
        var coll, response, options;
        last_fetched_filter = device_filter.model.calculate();
        last_fetched_filter['page[size]'] = 100;
        devices.fetch({
            remove: false,
            data: last_fetched_filter,
            success: function(coll, resp, opts){
                response = resp;
                options = opts;
            }
        }).then(function(){
                success_show_new_page(coll, response, options);
        });
    }

    last_fetched_filter = device_filter.model.calculate();
    fetch_devices();

    device_filter.on({
        'filter:clicked': function(e){
            set_filter(e);
        }
    });

    pagination_controls.on({
        'page': function(filter, models) {
            devices_list.collection.reset(models);
            devices_list.render();
        }
    });

        // Connect the ui events from the controls view to
        // items returned from the collection view's get_selected_models
        controls_view.on({
            'move': function() {
                var models = devices_list.get_selected_models();
                var groups = apiCh.request('development_devicegroup');

                var options = {
                    device_ids: _(models).pluck('id'),
                    cb: {
                        success : function(collection, resp){
                        fetch_devices();
                        },
                        error : function(resp){
                        fetch_devices();
                        }
                    }
                };
            // In the case of unassigned devices, a user
            // should be able to move a device to either
            // development or fixture device groups
            var omitted_types = [
            ];
            let types = groups.TYPES_MAP;
            
            if (!filter.devicegroup) {
                omitted_types.push(types.production);
            }
            // Only show factory devicegroups if we launched show devices
                // with a product and devicegroup, AND the devicegroup passed
                // is itself a factory devicegroup

            if (filter.devicegroup && filter.product) {
                switch (filter.devicegroup.type) {
                    case 'developemnt_devicegroup':
                        omitted_types.push(types.production, types.pre_production);
                        break;
                    case 'pre_production_devicegroup':
                        omitted_types.push(types.production, types.factoryfixture, types.pre_factoryfixture);
                        break;
                    case 'pre_factoryfixture_devicegroup':
                        omitted_types.push(types.production, types.pre_production);
                        break;
                    case 'factoryfixture_devicegroup':
                        omitted_types.push(types.production, types.pre_production);
                        break;
                    case 'production_devicegroup':
                        omitted_types.push(types.development, types.pre_production, types.factoryfixture, types.pre_factoryfixture);
                        break;
                    case 'pre_dut_devicegroup':
                        omitted_types.push(types.production, types.pre_production, types.factoryfixture, types.pre_factoryfixture, types.development, types.dut, types.pre_dut);
                        break;
                    case 'dut_devicegroup':
                        omitted_types.push(types.production, types.pre_production, types.factoryfixture, types.pre_factoryfixture, types.development, types.dut, types.pre_dut);
                        break;
                    default:
                        console.warn('no devicegroup type for devicegroup', filter.devicegroup);
                        omitted_types.push(types.production, types.pre_production);
                }

            } else {
                omitted_types.push(types.factoryfixture);
            }
            // always omit the dut types, they can never have devices assigned to them
            omitted_types.push(types.pre_dut);
            omitted_types.push(types.dut);

            var active = globalCh.request('active_account');
            var primary = globalCh.request('primary_account');

            if ( !active.permits(primary, 'update_development_device') ){
                omitted_types.push(types.development);
            }
            if ( !active.permits(primary, 'update_factory_device') ){
                omitted_types.push(types.factoryfixture);
            }
            if ( !active.permits(primary, 'update_pre_factory_device') ){
                omitted_types.push(types.pre_factoryfixture);
            }
            if ( !active.permits(primary, 'update_pre_production_device') ){
                omitted_types.push(types.pre_production);
            }
            if ( !active.permits(primary, 'update_production_device') ){
                omitted_types.push(types.production);
            }

                options.omitted_types = omitted_types;
                if (filter && filter.product) {
                    options.selected_product = filter.product;
                }
                if (filter && filter.devicegroup) {
                    options.selected_devicegroup = filter.devicegroup;
                }
                globalCh.request('show_move_devices_dialog', options);
            },
        'restart': function() {
            var models = devices_list.get_selected_models();
            var success_messages = [];
            var error_messages = [];
            var errors = 0; // a count of how many extra errors our request generates

            models.forEach(function(model){
                var id = model.id;
                model.restart({
                    success: function(model, status, resp) {
                        var message = {
                            id: id,
                            status: resp.status,
                            code: 'SUCCESS',
                            title: 'Restarted Device',
                            detail: 'Device successfully restarted.',
                            is_error: false,
                        };
                        success_messages.push(message);
                    },
                    error: function(resp){
                        _.each(resp.responseJSON.errors, function(err, index){
                            err.id = id;
                            err.is_error = true;
                            error_messages.push(err);
                            if ( index > 0 ){
                                errors += 1;
                            }
                        });
                    }
                }).done(function(){
                        if ( success_messages.length + error_messages.length === models.length + errors ){
                            show_response_results(success_messages, error_messages, models, '  successfully restarted.');
                        }
                });
            });
        },
        'unassign': function() {
            var models = devices_list.get_selected_models();
            var success_messages = [];
            var error_messages = [];
            var errors = 0; // a count of how many extra errors our request generates

            models.forEach(function(model){
                /*
                    * We need to unassign devices individually
                    * since they could all be from different devicegroups.
                    *
                    * The API can return more than one message per error,
                    * if it does, we need to track errors after the first
                    * for our accounting of when the transaction has completed.
                    * The first error is included as part of the models.length.
                    */

                var id = model.id;
                model.unassign({
                    success: function(model, status, resp){
                        var message = {
                            id: id,
                            status: resp.status,
                            code: 'SUCCESS',
                            title: 'Unassigned Device',
                            detail: 'Device successfully unassigned.',
                            is_error: false,
                        };
                        success_messages.push(message);
                    },
                    error: function(resp){
                        _.each(resp.responseJSON.errors, function(err, index){
                            err.id = id;
                            error_messages.push(err);
                            if ( index > 0 ){
                                errors += 1;
                            }
                        });
                    },
                    complete: function(){
                        if (success_messages.length + error_messages.length === models.length + errors) {
                            show_response_results(success_messages, error_messages, models, '  successfully unassigned.');
                        }
                    }
                });
            });
        },
        'toggle_actions': function(){
            this.toggle_actions(devices_list.get_selected_models().length);
        },
        'download': function() {
            var models = devices_list.get_selected_models();
            console.log('downloading models %o', models);
        }
    });
    show_devices_layout(filter);
}

export default show_devices;
